import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@parcel/ui';
import type * as React from 'react';
import { Hint } from '../hint.js';

/** Splits a badge list at `maxVisible`; the rest collapse into a "+N" badge. */
export function splitBadgeOverflow<T>(
  items: readonly T[],
  maxVisible: number,
): { visible: T[]; overflow: T[] } {
  if (items.length <= maxVisible) return { visible: [...items], overflow: [] };
  return { visible: items.slice(0, maxVisible), overflow: items.slice(maxVisible) };
}

export interface BadgeOverflowItem {
  key: string;
  label: string;
  variant: React.ComponentProps<typeof Badge>['variant'];
}

interface BadgeOverflowListProps {
  items: readonly BadgeOverflowItem[];
  /** Badges shown before collapsing into a "+N" badge with a tooltip. */
  maxVisible?: number;
  empty?: React.ReactNode;
  className?: string;
  badgeClassName?: string;
}

/**
 * Bounded badge list for table cells: renders at most `maxVisible` badges on
 * one line plus a "+N" badge whose tooltip lists the rest. Badges truncate to
 * fit a narrow cell while the "+N" always stays visible.
 */
export function BadgeOverflowList({
  items,
  maxVisible = 2,
  empty = <span className="text-muted-foreground">—</span>,
  className,
  badgeClassName,
}: BadgeOverflowListProps) {
  if (!items.length) {
    return <>{empty}</>;
  }

  const { visible, overflow } = splitBadgeOverflow(items, maxVisible);

  return (
    <span className={cn('flex min-w-0 max-w-full items-center gap-1', className)}>
      {visible.map((item) => (
        <Hint key={item.key} label={item.label}>
          <Badge
            variant={item.variant}
            className={cn('min-w-0 shrink whitespace-nowrap', badgeClassName)}
          >
            <span className="truncate">{item.label}</span>
          </Badge>
        </Hint>
      ))}
      {overflow.length ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="gray"
                tabIndex={0}
                className={cn('shrink-0 cursor-default whitespace-nowrap', badgeClassName)}
              >
                +{overflow.length}
              </Badge>
            </TooltipTrigger>
            {/* Hidden items render as the same badges as the cell, stacked one
                per line, so the tooltip scales to any +N. */}
            <TooltipContent className="px-2 py-2">
              <div className="flex flex-col items-start gap-1">
                {overflow.map((item) => (
                  <Badge key={item.key} variant={item.variant} className="whitespace-nowrap">
                    {item.label}
                  </Badge>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </span>
  );
}
